import React, { FormEvent, JSX, useEffect, useMemo, useState } from 'react';
import { IconEye, IconEyeClose } from '@/assets/icons';
import { Button, Checkbox, InputText, Select } from '@/components';
import { classNames } from '@/helpers/common';
import { UseFormReturn } from '@/hooks';
import { OptionProps } from '@/components/Select';
import { FormInputNameRegister } from '@/features/register/type';
import { REGISTER_STEP_FIELDS, RegisterStep } from '@/features/register/utils';
import { RegisterAlert } from '@/features/register/components/RegisterAlert';

interface RegisterUIProps extends Pick<
  UseFormReturn,
  'formData' | 'formErrors' | 'onChangeFormInput'
> {
  state: boolean;
  cpfValue?: string;
  shouldShowPasswordToText: boolean;
  shouldShowPasswordToText2: boolean;
  isTermsVisible: boolean;
  termsContent?: React.ReactNode;
  onToggleTerms: () => void;
  onChangeCEP: (value: string) => void;
  onTogglePasswordToText: () => void;
  onTogglePasswordToText2: () => void;
  onSubmit: (e: FormEvent) => void;
  onGoToLogin: () => void;
  onValidatePersonalData: () => Promise<boolean>;
  onSendContactConfirmation: () => Promise<boolean>;
  onConfirmContact: () => Promise<boolean>;
  onValidateFields: (fields: FormInputNameRegister[]) => boolean;
  registrationError?: string;
  contactConfirmationDestination?: string;
  contactConfirmationError?: string;
  onResendContactConfirmation: () => Promise<boolean>;
}

export const RegisterUI: React.FC<RegisterUIProps> = ({
  state,
  shouldShowPasswordToText,
  shouldShowPasswordToText2,
  formData,
  formErrors,
  // cpfValue,
  isTermsVisible,
  termsContent,
  onToggleTerms,
  onChangeFormInput,
  onChangeCEP,
  onSubmit,
  onTogglePasswordToText,
  onTogglePasswordToText2,
  onGoToLogin,
  onValidatePersonalData,
  onSendContactConfirmation,
  onConfirmContact,
  onValidateFields,
  registrationError,
  contactConfirmationDestination,
  contactConfirmationError,
  onResendContactConfirmation,
}) => {
  const [currentStep, setCurrentStep] = useState<RegisterStep>(RegisterStep.PERSONAL_DATA);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown === 0) return undefined;

    const timer = window.setTimeout(() => {
      setResendCooldown(previous => Math.max(0, previous - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const stateOptions: OptionProps[] = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapa' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceara' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espirito Santo' },
    { value: 'GO', label: 'Goias' },
    { value: 'MA', label: 'Maranhao' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Para' },
    { value: 'PB', label: 'Paraiba' },
    { value: 'PR', label: 'Parana' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piaui' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondonia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'Sao Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' },
  ];

  const steps = useMemo(
    () => [
      { id: RegisterStep.PERSONAL_DATA, title: 'Dados pessoais' },
      { id: RegisterStep.CONTACT, title: 'Contato' },
      { id: RegisterStep.CONTACT_CONFIRMATION, title: 'Confirmação' },
      { id: RegisterStep.ADDRESS, title: 'Endereço' },
      { id: RegisterStep.SECURITY, title: 'Senha' },
      { id: RegisterStep.TERMS, title: 'Termos' },
    ],
    [],
  );

  const hasAcceptedTerms =
    formData[FormInputNameRegister.terms] !== '' &&
    formData[FormInputNameRegister.terms] !== 'false';

  const isStepValid = (): boolean => {
    switch (currentStep) {
      case RegisterStep.PERSONAL_DATA:
        return (
          formData[FormInputNameRegister.document] !== '' &&
          formData[FormInputNameRegister.name] !== '' &&
          !formErrors.document &&
          !formErrors.name
          // formData[FormInputNameRegister.date] !== '' &&
          // formData[FormInputNameRegister.motherName] !== ''
        );

      case RegisterStep.CONTACT:
        return (
          formData[FormInputNameRegister.email] !== '' &&
          formData[FormInputNameRegister.phone] !== '' &&
          !formErrors.email &&
          !formErrors.phone
        );

      case RegisterStep.CONTACT_CONFIRMATION:
        return (
          formData[FormInputNameRegister.confirmationCode] !== '' && !formErrors.confirmationCode
        );

      case RegisterStep.ADDRESS:
        return (
          formData[FormInputNameRegister.zipCode] !== '' &&
          formData[FormInputNameRegister.state] !== '' &&
          formData[FormInputNameRegister.city] !== '' &&
          formData[FormInputNameRegister.district] !== '' &&
          formData[FormInputNameRegister.street] !== '' &&
          formData[FormInputNameRegister.addressnumber] !== ''
        );

      case RegisterStep.SECURITY:
        return (
          formData[FormInputNameRegister.password] !== '' &&
          formData[FormInputNameRegister.confirmPassword] !== ''
        );

      case RegisterStep.TERMS:
        return hasAcceptedTerms;

      default:
        return false;
    }
  };

  const handleNextStep = async (): Promise<void> => {
    if (!isStepValid()) return;
    if (!onValidateFields(REGISTER_STEP_FIELDS[currentStep])) return;

    if (currentStep === RegisterStep.PERSONAL_DATA) {
      const isValid = await onValidatePersonalData();
      if (!isValid) return;
    }

    if (currentStep === RegisterStep.CONTACT) {
      const wasSent = await onSendContactConfirmation();

      if (!wasSent) return;
      setResendCooldown(30);
    }

    if (currentStep === RegisterStep.CONTACT_CONFIRMATION) {
      const wasConfirmed = await onConfirmContact();

      if (!wasConfirmed) return;
    }

    setCurrentStep(prev => (prev < RegisterStep.TERMS ? prev + 1 : prev));
  };

  const handleResendContactConfirmation = async (): Promise<void> => {
    const wasSent = await onResendContactConfirmation();
    if (wasSent) setResendCooldown(30);
  };

  const handlePreviousStep = (): void => {
    setCurrentStep(prev => (prev > RegisterStep.PERSONAL_DATA ? prev - 1 : prev));
  };

  const renderStepContent = (): JSX.Element => {
    switch (currentStep) {
      case RegisterStep.PERSONAL_DATA:
        return (
          <div className="grid grid-cols-1 gap-5">
            <div>
              <InputText
                name="document"
                label="CPF"
                placeholder="123.456.789-00"
                maxLength={14}
                value={formData[FormInputNameRegister.document]}
                onChange={e => onChangeFormInput(FormInputNameRegister.document)(e.target.value)}
                error={formErrors.document && formErrors.document[0]}
              />
            </div>

            <div>
              <InputText
                name="name"
                label="Nome completo"
                placeholder="Maria da Silva"
                value={formData[FormInputNameRegister.name]}
                onChange={e => onChangeFormInput(FormInputNameRegister.name)(e.target.value)}
                error={formErrors.name && formErrors.name[0]}
              />
            </div>
            {/* <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">
                Dados já validados
              </p> */}

            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    CPF
                  </span>
                  <span className="mt-1 block font-semibold text-gray-900">
                    {cpfValue || formData[FormInputNameRegister.document] || '-'}
                  </span>
                </div>

                <div>
                  <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    Nome
                  </span>
                  <span className="mt-1 block font-semibold text-gray-900">
                    {formData[FormInputNameRegister.name] || '-'}
                  </span>
                </div>
              </div> */}
            {/* </div> */}
          </div>
        );

      case RegisterStep.CONTACT:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {contactConfirmationError && (
              <div className="md:col-span-2">
                <RegisterAlert
                  title="Não foi possível enviar o código"
                  message={contactConfirmationError}
                />
              </div>
            )}

            <div className="md:col-span-2">
              <InputText
                name="email"
                label="E-mail"
                placeholder="meuemail@email.com"
                value={formData[FormInputNameRegister.email]}
                onChange={e => onChangeFormInput(FormInputNameRegister.email)(e.target.value)}
                error={formErrors.email && formErrors.email[0]}
              />
            </div>

            <div className="md:col-span-2">
              <InputText
                name="phone"
                label="Telefone"
                placeholder="(00) 0 0000-0000"
                value={formData[FormInputNameRegister.phone]}
                onChange={e => onChangeFormInput(FormInputNameRegister.phone)(e.target.value)}
                error={formErrors.phone && formErrors.phone[0]}
              />
            </div>
          </div>
        );
      case RegisterStep.CONTACT_CONFIRMATION:
        return (
          <div className="grid grid-cols-1 gap-5">
            {contactConfirmationError && (
              <RegisterAlert
                title="Não foi possível reenviar o código"
                message={contactConfirmationError}
              />
            )}

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm text-gray-600">
                Enviamos um código de confirmação para{' '}
                <strong>{contactConfirmationDestination || 'o contato informado'}</strong>.
              </p>
            </div>

            <InputText
              name="confirmationCode"
              label="Código de confirmação"
              placeholder="Digite o código recebido"
              value={formData[FormInputNameRegister.confirmationCode]}
              onChange={e =>
                onChangeFormInput(FormInputNameRegister.confirmationCode)(e.target.value)
              }
              error={formErrors.confirmationCode && formErrors.confirmationCode[0]}
            />

            <div className="flex justify-center">
              <Button
                type="button"
                title={resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : 'Reenviar código'}
                buttonStyle="outlined"
                size="xlg"
                className="w-full md:w-[220px]"
                onClick={handleResendContactConfirmation}
                disabled={state || resendCooldown > 0}
              />
            </div>
          </div>
        );

      case RegisterStep.ADDRESS:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <InputText
                name="zipCode"
                label="CEP"
                placeholder="00000-000"
                value={formData[FormInputNameRegister.zipCode]}
                maxLength={9}
                onChange={e => {
                  onChangeFormInput(FormInputNameRegister.zipCode)(e.target.value);
                  onChangeCEP(e.target.value);
                }}
                error={formErrors.zipCode && formErrors.zipCode[0]}
              />
            </div>

            <div>
              <Select
                name="state"
                label="Estado"
                placeholder="Selecione..."
                value={formData[FormInputNameRegister.state]}
                onChange={e =>
                  onChangeFormInput(FormInputNameRegister.state)(e?.target.value as string)
                }
                error={formErrors.state && formErrors.state[0]}
                options={stateOptions}
              />
            </div>

            <div>
              <InputText
                name="city"
                label="Cidade"
                value={formData[FormInputNameRegister.city]}
                onChange={e => onChangeFormInput(FormInputNameRegister.city)(e.target.value)}
                error={formErrors.city && formErrors.city[0]}
              />
            </div>

            <div>
              <InputText
                name="district"
                label="Bairro"
                value={formData[FormInputNameRegister.district]}
                onChange={e => onChangeFormInput(FormInputNameRegister.district)(e.target.value)}
                error={formErrors.district && formErrors.district[0]}
              />
            </div>

            <div className="md:col-span-2">
              <InputText
                name="street"
                label="Rua/Avenida"
                value={formData[FormInputNameRegister.street]}
                onChange={e => onChangeFormInput(FormInputNameRegister.street)(e.target.value)}
                error={formErrors.street && formErrors.street[0]}
              />
            </div>

            <div>
              <InputText
                name="addressnumber"
                label="Número"
                value={formData[FormInputNameRegister.addressnumber]}
                onChange={e =>
                  onChangeFormInput(FormInputNameRegister.addressnumber)(e.target.value)
                }
                error={formErrors.addressnumber && formErrors.addressnumber[0]}
              />
            </div>

            <div>
              <InputText
                name="complement"
                label="Complemento (opcional)"
                value={formData[FormInputNameRegister.complement]}
                onChange={e => onChangeFormInput(FormInputNameRegister.complement)(e.target.value)}
                error={formErrors.complement && formErrors.complement[0]}
              />
            </div>
          </div>
        );

      case RegisterStep.SECURITY:
        return (
          <div className="grid grid-cols-1 gap-5">
            <div>
              <InputText
                name="password"
                label="Senha"
                type={shouldShowPasswordToText ? 'text' : 'password'}
                placeholder="**********"
                value={formData[FormInputNameRegister.password]}
                maxLength={15}
                onChange={e => onChangeFormInput(FormInputNameRegister.password)(e.target.value)}
                error={formErrors.password && formErrors.password[0]}
                renderForward={
                  <button
                    className={classNames(
                      formErrors.password && formErrors.password[0]
                        ? 'right-0 bottom-7'
                        : 'right-0 bottom-3',
                      'absolute cursor-pointer mr-4',
                    )}
                    onClick={onTogglePasswordToText}
                    type="button"
                  >
                    <div className="h-8 flex flex-col justify-center items-center">
                      {shouldShowPasswordToText ? (
                        <img src={IconEye} alt="Mostrar senha" />
                      ) : (
                        <img src={IconEyeClose} alt="Ocultar senha" />
                      )}
                    </div>
                  </button>
                }
              />
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="mb-3 text-sm font-semibold text-gray-700">Sua senha deve conter:</p>
              <ul className="ml-5 list-disc text-sm text-gray-600 space-y-1">
                <li>No mínimo 8 caracteres</li>
                <li>Pelo menos um número</li>
                <li>Pelo menos um caractere maiúsculo</li>
              </ul>
            </div>

            <div>
              <InputText
                name="confirmPassword"
                label="Confirme a sua senha"
                type={shouldShowPasswordToText2 ? 'text' : 'password'}
                placeholder="**********"
                value={formData[FormInputNameRegister.confirmPassword]}
                maxLength={15}
                onChange={e =>
                  onChangeFormInput(FormInputNameRegister.confirmPassword)(e.target.value)
                }
                error={formErrors.confirmPassword && formErrors.confirmPassword[0]}
                renderForward={
                  <button
                    className={classNames(
                      formErrors.confirmPassword && formErrors.confirmPassword[0]
                        ? 'right-0 bottom-7'
                        : 'right-0 bottom-3',
                      'absolute cursor-pointer mr-4',
                    )}
                    onClick={onTogglePasswordToText2}
                    type="button"
                  >
                    <div className="h-8 flex flex-col justify-center items-center">
                      {shouldShowPasswordToText2 ? (
                        <img src={IconEye} alt="Mostrar senha" />
                      ) : (
                        <img src={IconEyeClose} alt="Ocultar senha" />
                      )}
                    </div>
                  </button>
                }
              />
            </div>
          </div>
        );

      case RegisterStep.TERMS:
        return (
          <div className="space-y-5">
            {registrationError && (
              <RegisterAlert title="Não foi possível criar sua conta" message={registrationError} />
            )}

            <div
              className="rounded-xl border border-gray-200 bg-gray-50 p-5"
              id="termsAndConditions"
            >
              <Checkbox
                name="terms"
                value={formData[FormInputNameRegister.terms]}
                className="mb-[20px]"
                onChange={checked => onChangeFormInput(FormInputNameRegister.terms)(checked)}
                error={formErrors.terms && formErrors.terms[0]}
                renderForward={
                  <div className="ml-3 text-sm text-gray-700 leading-6">
                    Eu aceito os
                    <a
                      className="mx-1 font-semibold text-red-600 underline underline-offset-2"
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        onToggleTerms();
                      }}
                    >
                      Termos e Política de Privacidade
                    </a>
                    do site
                  </div>
                }
              />

              <button
                type="button"
                className="mt-2 text-sm font-medium text-red-600 underline underline-offset-2"
                onClick={onToggleTerms}
              >
                {isTermsVisible ? 'Ocultar termos' : 'Ler termos completos'}
              </button>
            </div>

            {isTermsVisible && (
              <div className="rounded-xl border border-gray-200 bg-white p-5 max-h-[420px] overflow-y-auto">
                <div className="mb-4 border-b border-gray-100 pb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Termos e Política de Privacidade
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Leia com atenção antes de concluir o cadastro.
                  </p>
                </div>

                <div className="text-sm leading-6 text-gray-700">{termsContent}</div>
              </div>
            )}
          </div>
        );

      default:
        return <></>;
    }
  };

  const isLastStep = currentStep === RegisterStep.TERMS;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="mx-auto w-full max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900">Crie sua conta</h1>
          <p className="mt-2 text-sm text-gray-600">
            Preencha seus dados para continuar o cadastro
          </p>
        </div>

        <div className="rounded-2xl bg-white shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {steps.map(step => {
                const active = currentStep === step.id;
                const completed = currentStep > step.id;

                return (
                  <div key={step.id} className="flex flex-col items-center text-center">
                    <div
                      className={classNames(
                        'mb-2 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-all',
                        active ? 'bg-red-600 text-white' : '',
                        completed ? 'bg-green-600 text-white' : '',
                        !active && !completed
                          ? 'bg-gray-100 text-gray-500 border border-gray-200'
                          : '',
                      )}
                    >
                      {step.id + 1}
                    </div>

                    <span
                      className={classNames(
                        'text-xs md:text-sm font-medium',
                        active ? 'text-red-600' : '',
                        completed ? 'text-green-600' : '',
                        !active && !completed ? 'text-gray-500' : '',
                      )}
                    >
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <form onSubmit={onSubmit} className="px-6 py-6 md:px-8 md:py-8">
            <div className="mb-8">{renderStepContent()}</div>

            <div className="flex flex-col-reverse md:flex-row gap-3 md:justify-between md:items-center">
              <div>
                {currentStep > RegisterStep.PERSONAL_DATA && (
                  <Button
                    type="button"
                    title="Voltar"
                    buttonStyle="outlined"
                    size="xlg"
                    className="w-full md:w-[160px]"
                    onClick={handlePreviousStep}
                    disabled={state}
                  />
                )}
              </div>

              <div className="w-full md:w-auto">
                {!isLastStep ? (
                  <Button
                    type="button"
                    title="Continuar"
                    buttonStyle="primary"
                    size="xlg"
                    className="w-full md:w-[200px]"
                    onClick={handleNextStep}
                    disabled={!isStepValid() || state}
                  />
                ) : (
                  <Button
                    type="submit"
                    title="Criar a minha conta"
                    buttonStyle="primary"
                    size="xlg"
                    className="w-full md:w-[240px]"
                    disabled={!isStepValid() || state}
                  />
                )}
              </div>
            </div>

            <div className="mt-8 border-t border-gray-100 pt-6 text-center">
              <span className="text-sm text-gray-500">Já tem uma conta?</span>
              <a
                className="ml-2 text-sm font-semibold text-red-600 underline underline-offset-2"
                href="#"
                onClick={e => {
                  e.preventDefault();
                  onGoToLogin();
                }}
              >
                Entrar
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
